import { useEffect, useRef } from 'react';
import { useShogunchop } from '../providers/ShogunChopProvider';

/** Trailing debounce for watcher bursts: the daemon already coalesces at
 *  800 ms; this collapses back-to-back frames (e.g. a git checkout touching
 *  many files across passes) into one refresh. */
const DEBOUNCE_MS = 1000;

/**
 * Local-workspace file-change trigger:
 * subscribe to the desktop daemon's loopback `/events` stream and invoke
 * `onFilesChanged` — debounced 1 s trailing-edge — whenever the daemon's
 * watcher reports `workspace:filesChanged` for THIS workspace. The daemon
 * emits after its debounced incremental index pass, so this catches IDE
 * edits, git operations, `touch` from a terminal — anything.
 *
 * Only meaningful for LOCAL workspaces on desktop: pass `enabled` accordingly.
 * On web `shogunchop.resource` is null (no daemon) and this never subscribes —
 * same shape as `useIndexingProgressStream`.
 */
export function useWorkspaceFilesChanged({
  workspaceId,
  enabled,
  onFilesChanged,
}: {
  workspaceId: string;
  enabled: boolean;
  onFilesChanged: () => void;
}): void {
  const shogunchop = useShogunchop();
  const onFilesChangedRef = useRef(onFilesChanged);
  onFilesChangedRef.current = onFilesChanged;

  useEffect(() => {
    if (!enabled) return;
    const sc = shogunchop.resource;
    if (!sc) return; // web / no daemon — nothing to subscribe to.

    const stream = sc.sharedEvents();

    let timer: ReturnType<typeof setTimeout> | null = null;
    stream.on((event) => {
      if (event.event !== 'workspace:filesChanged') return;
      if (event.data.workspace_id !== workspaceId) return;
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        onFilesChangedRef.current();
      }, DEBOUNCE_MS);
    });

    return () => {
      stream.release();
      if (timer !== null) clearTimeout(timer);
    };
  }, [enabled, shogunchop.resource, workspaceId]);
}
